import React, { useEffect, useMemo, useState } from 'react';
import { GripVertical, Lock, Search } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Checkbox } from '../../../components/ui/checkbox';
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '../../../components/ui/sheet';
import { ALL_TABLE_FIELDS, DEFAULT_TABLE_FIELDS, LOCKED_FIELDS } from '../constants/taskFields';
import { dueDateColumnLabel } from '../services/taskTimezoneUtils';

export function TaskManageFieldsDrawer({
  open,
  onOpenChange,
  fields,
  timezone,
  onApply,
}) {
  const [draft, setDraft] = useState(fields || DEFAULT_TABLE_FIELDS);
  const [query, setQuery] = useState('');
  const [dragKey, setDragKey] = useState(null);

  useEffect(() => {
    if (open) setDraft(fields?.length ? fields : DEFAULT_TABLE_FIELDS);
  }, [open, fields]);

  const labelFor = (key) => {
    if (key === 'due_at') return dueDateColumnLabel(timezone);
    if (key === 'created_at' || key === 'updated_at') {
      const base = ALL_TABLE_FIELDS.find((f) => f.key === key)?.label || key;
      const abbr = dueDateColumnLabel(timezone).match(/\(([^)]+)\)/)?.[1];
      return abbr ? `${base} (${abbr})` : base;
    }
    return ALL_TABLE_FIELDS.find((f) => f.key === key)?.label || key;
  };

  const visibleSet = useMemo(() => new Set(draft), [draft]);

  const filtered = ALL_TABLE_FIELDS.filter((f) => (
    !query || f.label.toLowerCase().includes(query.toLowerCase()) || f.key.includes(query.toLowerCase())
  ));

  const toggleField = (key) => {
    if (LOCKED_FIELDS.includes(key)) return;
    setDraft((prev) => (
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    ));
  };

  const onDragStart = (key) => {
    if (LOCKED_FIELDS.includes(key)) return;
    setDragKey(key);
  };

  const onDrop = (targetKey) => {
    if (!dragKey || dragKey === targetKey) return;
    if (LOCKED_FIELDS.includes(targetKey) || LOCKED_FIELDS.includes(dragKey)) {
      setDragKey(null);
      return;
    }
    setDraft((prev) => {
      const next = prev.filter((k) => k !== dragKey);
      const idx = next.indexOf(targetKey);
      if (idx < 0) return [...next, dragKey];
      next.splice(idx, 0, dragKey);
      return next;
    });
    setDragKey(null);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col p-0 sm:max-w-md" data-testid="task-manage-fields">
        <SheetHeader className="border-b border-border px-6 py-4 text-left">
          <SheetTitle>Manage fields</SheetTitle>
        </SheetHeader>

        <div className="space-y-4 overflow-y-auto px-6 py-4">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              className="pl-8"
              placeholder="Search fields"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <div className="space-y-1">
            <p className="text-xs font-semibold uppercase text-muted-foreground">Visible columns</p>
            {draft.map((key) => (
              <div
                key={key}
                className="flex items-center gap-2 rounded-md border border-border px-2 py-2"
                draggable={!LOCKED_FIELDS.includes(key)}
                onDragStart={() => onDragStart(key)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDrop(key)}
              >
                {LOCKED_FIELDS.includes(key) ? (
                  <Lock className="h-4 w-4 text-muted-foreground" aria-label="Locked column" />
                ) : (
                  <GripVertical className="h-4 w-4 text-muted-foreground" aria-hidden />
                )}
                <span className="flex-1 text-sm">{labelFor(key)}</span>
                <Checkbox
                  checked
                  disabled={LOCKED_FIELDS.includes(key)}
                  onCheckedChange={() => toggleField(key)}
                  aria-label={`Toggle ${labelFor(key)}`}
                />
              </div>
            ))}
          </div>

          <div className="space-y-1">
            <p className="text-xs font-semibold uppercase text-muted-foreground">Available fields</p>
            {filtered
              .filter((f) => !visibleSet.has(f.key))
              .map((field) => (
                <div key={field.key} className="flex items-center gap-2 rounded-md px-2 py-2 hover:bg-muted/40">
                  <span className="flex-1 text-sm">{labelFor(field.key)}</span>
                  <Checkbox
                    checked={false}
                    onCheckedChange={() => toggleField(field.key)}
                    aria-label={`Show ${labelFor(field.key)}`}
                  />
                </div>
              ))}
          </div>
        </div>

        <SheetFooter className="mt-auto flex flex-row justify-end gap-2 border-t border-border px-6 py-4">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            type="button"
            onClick={() => {
              const lockedFirst = [
                ...LOCKED_FIELDS.filter((k) => draft.includes(k) || true),
                ...draft.filter((k) => !LOCKED_FIELDS.includes(k)),
              ];
              // Ensure locked fields present exactly once, preserving relative optional order
              const ensured = [
                'status',
                'title',
                ...draft.filter((k) => !LOCKED_FIELDS.includes(k) && k !== 'actions'),
                'actions',
              ].filter((k, i, arr) => arr.indexOf(k) === i);
              onApply?.(ensured);
            }}
          >
            Apply
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

export default TaskManageFieldsDrawer;
